import { useState, useEffect } from 'react';
import axios from 'axios';
import { Heart, Bookmark, Image as ImageIcon, Video as VideoIcon, Smile, Plus } from 'lucide-react';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import EnhancedPostCard from '@/components/post/EnhancedPostCard';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const stories = [
  { id: 1, name: 'Pandit Sharma', image: 'https://images.unsplash.com/photo-1762838105804-c3264c29a390?w=200&h=200&fit=crop' },
  { id: 2, name: 'Devotee Priya', image: 'https://images.unsplash.com/photo-1761383396518-e2e30968a25f?w=200&h=200&fit=crop' },
  { id: 3, name: 'Temple Updates', image: 'https://images.unsplash.com/photo-1767533427730-cc8791c14558?w=200&h=200&fit=crop' },
  { id: 4, name: 'Mantra Daily', image: 'https://images.unsplash.com/photo-1723707358389-7b47690dff16?w=200&h=200&fit=crop' },
  { id: 5, name: 'Bhakti Music', image: 'https://images.unsplash.com/photo-1759592370294-30f83cf6a609?w=200&h=200&fit=crop' },
];

export default function NewHomePage({ user }) {
  const navigate = useNavigate();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [postText, setPostText] = useState('');

  useEffect(() => {
    fetchPosts();
  }, []);

  const fetchPosts = async () => {
    try {
      const token = localStorage.getItem('dharmbook_token');
      const response = await axios.get(`${API}/posts?skip=0&limit=20`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      setPosts(response.data);
    } catch (error) {
      toast.error('Failed to load posts');
    } finally {
      setLoading(false);
    }
  };

  const handleLike = async (postId) => {
    try {
      const token = localStorage.getItem('dharmbook_token');
      const response = await axios.post(
        `${API}/posts/${postId}/like`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setPosts((prev) =>
        prev.map((post) =>
          post.id === postId
            ? {
                ...post,
                is_liked: response.data.liked,
                likes_count: response.data.liked ? post.likes_count + 1 : post.likes_count - 1,
              }
            : post
        )
      );
    } catch (error) {
      toast.error('Failed to like post');
    }
  };

  const handleSave = async (postId) => {
    try {
      const token = localStorage.getItem('dharmbook_token');
      const response = await axios.post(
        `${API}/posts/${postId}/save`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setPosts((prev) =>
        prev.map((post) =>
          post.id === postId ? { ...post, is_saved: response.data.saved } : post
        )
      );

      toast.success(response.data.saved ? 'Post saved' : 'Post unsaved');
    } catch (error) {
      toast.error('Failed to save post');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center">
          <div className="text-4xl text-[#FF9933] mb-2">ॐ</div>
          <p className="text-slate-600">Loading feed...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto px-4 py-4 pb-20 md:pb-4">
      {/* Stories Section */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-4">
        <div className="flex gap-3 overflow-x-auto scrollbar-hide">
          {/* Create Story */}
          <div className="flex flex-col items-center flex-shrink-0 cursor-pointer group">
            <div className="relative w-20 h-20 rounded-xl overflow-hidden bg-gradient-to-br from-orange-100 to-orange-200 flex items-center justify-center group-hover:opacity-90 transition-opacity">
              <Plus className="w-8 h-8 text-[#FF9933]" />
              <div className="absolute bottom-0 right-0 w-6 h-6 bg-[#FF9933] rounded-full flex items-center justify-center border-2 border-white">
                <Plus className="w-4 h-4 text-white" />
              </div>
            </div>
            <span className="text-xs mt-1 text-gray-700 font-medium">Create</span>
          </div>

          {/* Story Items */}
          {stories.map((story) => (
            <div key={story.id} className="flex flex-col items-center flex-shrink-0 cursor-pointer group">
              <div className="w-20 h-20 rounded-xl overflow-hidden ring-4 ring-[#FF9933] p-1 group-hover:ring-[#E68A00] transition-all">
                <img src={story.image} alt={story.name} className="w-full h-full object-cover rounded-lg" />
              </div>
              <span className="text-xs mt-1 text-gray-700 font-medium truncate w-20 text-center">{story.name}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Create Post Card */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-4">
        <div className="flex gap-3">
          <img src={user?.avatar} alt={user?.name} className="w-10 h-10 rounded-full object-cover" />
          <button
            onClick={() => navigate('/create')}
            className="flex-1 px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-full text-left text-gray-500 transition-colors"
          >
            What's on your mind, {user?.name}?
          </button>
        </div>
        <div className="flex items-center justify-around mt-3 pt-3 border-t border-gray-200">
          <button className="flex items-center gap-2 px-4 py-2 hover:bg-gray-50 rounded-lg transition-colors">
            <VideoIcon className="w-5 h-5 text-red-500" />
            <span className="text-sm font-medium text-gray-700">Video</span>
          </button>
          <button className="flex items-center gap-2 px-4 py-2 hover:bg-gray-50 rounded-lg transition-colors">
            <ImageIcon className="w-5 h-5 text-green-500" />
            <span className="text-sm font-medium text-gray-700">Photo</span>
          </button>
          <button className="flex items-center gap-2 px-4 py-2 hover:bg-gray-50 rounded-lg transition-colors">
            <Smile className="w-5 h-5 text-yellow-500" />
            <span className="text-sm font-medium text-gray-700">Feeling</span>
          </button>
        </div>
      </div>

      {/* Posts Feed */}
      {posts.map((post, index) => (
        <div
          key={post.id}
          data-testid={`post-${index}`}
          className="bg-white rounded-lg shadow-sm border border-gray-200 mb-4"
        >
          {/* Post Header */}
          <div className="flex items-center gap-3 p-4">
            <img
              src={post.user_avatar}
              alt={post.user_name}
              className="w-10 h-10 rounded-full object-cover"
            />
            <div className="flex-1">
              <div className="flex items-center gap-2">
                <h3 className="font-semibold text-gray-900">{post.user_name}</h3>
                {post.user_role === 'pandit' && (
                  <span className="px-2 py-0.5 bg-[#FF9933] text-white text-xs rounded font-medium">Verified</span>
                )}
                {post.user_role === 'creator' && (
                  <span className="px-2 py-0.5 bg-purple-500 text-white text-xs rounded font-medium">Creator</span>
                )}
              </div>
              <p className="text-xs text-gray-500">{new Date(post.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} · 🌍</p>
            </div>
            <button className="text-gray-400 hover:text-gray-600">
              <span className="text-2xl">⋯</span>
            </button>
          </div>

          {/* Post Content */}
          <div className="px-4 pb-3">
            {post.post_type === 'mantra' && (
              <div className="bg-gradient-to-br from-orange-50 to-amber-50 rounded-xl p-6 mb-3 border border-orange-100">
                <p className="text-lg text-gray-800 text-center mb-2 font-semibold" style={{ fontFamily: 'Merriweather, serif' }}>
                  {post.content}
                </p>
                {post.mantra_meaning && (
                  <p className="text-sm text-gray-600 text-center italic">Meaning: {post.mantra_meaning}</p>
                )}
              </div>
            )}

            {post.post_type === 'text' && (
              <p className="text-gray-800 leading-relaxed">{post.content}</p>
            )}

            {(post.post_type === 'video' || post.post_type === 'audio') && post.media_url && (
              <div className="mb-3">
                <p className="text-gray-800 leading-relaxed mb-3">{post.content}</p>
                {post.post_type === 'video' && (
                  <div className="aspect-video rounded-lg overflow-hidden bg-gray-900">
                    <iframe
                      src={post.media_url.replace('watch?v=', 'embed/')}
                      className="w-full h-full"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                    ></iframe>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Post Stats */}
          <div className="px-4 pb-2 flex items-center justify-between text-sm text-gray-500">
            <span>{post.likes_count} Namaste</span>
            <span>23 shares</span>
          </div>

          {/* Post Actions */}
          <div className="border-t border-gray-200 px-2 py-1 flex items-center justify-around">
            <button
              data-testid={`like-btn-${index}`}
              onClick={() => handleLike(post.id)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors hover:bg-gray-50 ${
                post.is_liked ? 'text-[#FF9933]' : 'text-gray-600'
              }`}
            >
              <Heart
                className="w-5 h-5"
                fill={post.is_liked ? '#FF9933' : 'none'}
                stroke={post.is_liked ? '#FF9933' : 'currentColor'}
              />
              <span className="text-sm font-medium">Namaste</span>
            </button>

            <button
              data-testid={`save-btn-${index}`}
              onClick={() => handleSave(post.id)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors hover:bg-gray-50 ${
                post.is_saved ? 'text-[#FF9933]' : 'text-gray-600'
              }`}
            >
              <Bookmark
                className="w-5 h-5"
                fill={post.is_saved ? '#FF9933' : 'none'}
                stroke={post.is_saved ? '#FF9933' : 'currentColor'}
              />
              <span className="text-sm font-medium">Save</span>
            </button>
          </div>
        </div>
      ))}

      {posts.length === 0 && (
        <div className="text-center py-12 bg-white rounded-lg shadow-sm">
          <p className="text-slate-600">No posts yet. Be the first to share!</p>
        </div>
      )}
    </div>
  );
}
